import {exhaustive} from '@freckle/exhaustive'
import {useSafeCallback, useSafeEffectExtraDeps} from '@freckle/react-hooks'
import type {CallbackFn} from '@freckle/react-hooks'
import type {OperationLocationT} from '@freckle/educator-entities/ts/common/helpers/ajax'
import {
  type ArtifactDataState,
  useOperation,
} from '@freckle/educator-entities/ts/common/hooks/operations/use-operation'
import type {ArtifactData} from '@freckle/educator-entities/ts/common/models/artifact'
import {Flex} from '@freckle/educator-materials/ts/components/flex'
import {Modal, Header, Body} from '@freckle/educator-materials/ts/components/modal'
import {Spinner} from '@freckle/educator-materials/ts/components/spinner'
import {Text} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'

type Props<TOperationLocation> = {
  operationLocation: TOperationLocation
  i18nKeys: {
    title: string
    heading: string
    body: string
  }
  onSuccess: CallbackFn<(a: ArtifactData[]) => void>
  onFailure: CallbackFn<() => void>
  // Like `finally` in `try`/catch, called whenever the operation is finished
  // whether success or failure.
  onFinish?: CallbackFn<() => void>
}

const PendingOperation = ({
  operationLocation,
  onSuccess,
  onFailure,
  onFinish: mOnFinish,
  i18nKeys,
}: Props<OperationLocationT>) => {
  const {operation, artifactData} = useOperation({operationLocation})
  const {t} = useSafeTranslation()

  const noopOnFinish = useSafeCallback(() => () => {}, [])

  const onFinish = mOnFinish ?? noopOnFinish

  const operationStatus = operation?.status

  useSafeEffectExtraDeps<{
    onFailure: () => void
    onFinish: () => void
  }>(
    ({onFailure, onFinish}) => {
      switch (operationStatus) {
        case undefined:
        case 'not_started':
        case 'running':
        case 'succeeded':
          onFinish()
          return
        case 'failed':
          onFinish()
          return onFailure()
        default:
          return exhaustive(operationStatus)
      }
    },
    [operationStatus],
    {onFailure, onFinish},
  )

  useSafeEffectExtraDeps(
    ({
      onSuccess,
      onFailure,
      artifactData,
    }: {
      onSuccess: (a: ArtifactData[]) => void
      onFailure: () => void
      artifactData: ArtifactDataState
    }) => {
      switch (artifactData.tag) {
        case 'fetching':
        case 'not_started':
          return
        case 'success':
          return onSuccess(artifactData.artifactData)
        case 'failed':
          return onFailure()
        default:
          return exhaustive(artifactData)
      }
    },
    [],
    {
      onSuccess,
      onFailure,
      artifactData: {
        value: artifactData,
        comparator: (a1, a2) => a1.tag === a2.tag,
      },
    },
  )

  switch (operationStatus) {
    case undefined:
    case 'not_started':
    case 'running':
      return (
        <PendingModal
          title={t(i18nKeys.title)}
          heading={t(i18nKeys.heading)}
          body={t(i18nKeys.body)}
        />
      )
    case 'succeeded':
      switch (artifactData.tag) {
        case 'fetching':
        case 'not_started':
          return (
            <PendingModal
              title={t(i18nKeys.title)}
              heading={t(i18nKeys.heading)}
              body={t(i18nKeys.body)}
            />
          )
        case 'success':
        case 'failed':
          return null
        default:
          return exhaustive(artifactData)
      }
    case 'failed':
      return null
    default:
      return exhaustive(operationStatus)
  }
}

const PendingModal = ({title, heading, body}: {title: string; heading: string; body: string}) => (
  <Modal show onHide={() => {}} height="medium-small">
    <Header>{title}</Header>
    <Body>
      <Flex flexDirection="column">
        <Spinner />
        <Text style="subtitle-2">{heading}</Text>
        <Text style="body-2">{body}</Text>
      </Flex>
    </Body>
  </Modal>
)

export const PendingOperationWhenLocation = ({
  operationLocation,
  ...rest
}: Props<OperationLocationT | null>) =>
  operationLocation === null ? null : (
    <PendingOperation operationLocation={operationLocation} {...rest} />
  )
